import { useEffect, useRef, useState } from "react";
import "./game-display.css";

type InstalledMode = "browser" | "standalone" | "fullscreen";
type LandscapeOrientation = ScreenOrientation & { lock?: (orientation: "landscape") => Promise<void> };

const portraitQuery = "(max-width: 959px) and (orientation: portrait)";
const manualRotation = "若未自动横屏，请关闭系统的竖屏方向锁定，再手动横转设备。";
const fullscreenFailure = "浏览器未能切换全屏。请允许全屏后重试，或从浏览器菜单安装到主屏幕后打开。";

function installedMode(): InstalledMode {
  if (window.matchMedia("(display-mode: standalone)").matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true) return "standalone";
  if (!document.fullscreenElement && window.matchMedia("(display-mode: fullscreen)").matches) return "fullscreen";
  return "browser";
}

export function GameDisplayControls({ playing, onLeave }: {
  playing: boolean;
  onLeave: () => void;
}) {
  const [fullscreen, setFullscreen] = useState(() => Boolean(document.fullscreenElement));
  const [installed, setInstalled] = useState<InstalledMode>(installedMode);
  const [helpOpen, setHelpOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const mounted = useRef(false);
  const pending = useRef(false);
  const lockedOrientation = useRef<LandscapeOrientation | null>(null);
  const fullscreenSupported = Boolean(document.fullscreenEnabled
    && typeof document.documentElement.requestFullscreen === "function");
  const canToggleFullscreen = fullscreen || (fullscreenSupported && installed !== "fullscreen");

  function unlockOrientation(): boolean {
    const orientation = lockedOrientation.current;
    lockedOrientation.current = null;
    if (!orientation) return true;
    try {
      orientation.unlock();
      return true;
    } catch {
      return false;
    }
  }

  useEffect(() => {
    mounted.current = true;
    const portraitMedia = window.matchMedia(portraitQuery);
    const standaloneMedia = window.matchMedia("(display-mode: standalone)");
    const fullscreenMedia = window.matchMedia("(display-mode: fullscreen)");
    const updatePortrait = () => {
      if (!portraitMedia.matches) setHelpOpen(false);
    };
    const updateInstalled = () => {
      // Firefox can report display-mode before fullscreenElement changes.
      // Preserve the launch mode throughout our native fullscreen transition.
      if (!pending.current && !document.fullscreenElement) setInstalled(installedMode());
    };
    const updateFullscreen = () => {
      const active = Boolean(document.fullscreenElement);
      setFullscreen(active);
      if (!active && !unlockOrientation()) {
        setMessage(`已退出全屏，但系统未能解除横屏锁定。${manualRotation}`);
        setHelpOpen(true);
      }
      updateInstalled();
    };
    const fullscreenError = () => {
      setFullscreen(Boolean(document.fullscreenElement));
      setMessage(fullscreenFailure);
      setHelpOpen(true);
    };
    portraitMedia.addEventListener("change", updatePortrait);
    standaloneMedia.addEventListener("change", updateInstalled);
    fullscreenMedia.addEventListener("change", updateInstalled);
    document.addEventListener("fullscreenchange", updateFullscreen);
    document.addEventListener("fullscreenerror", fullscreenError);
    window.addEventListener("pageshow", updateInstalled);
    return () => {
      mounted.current = false;
      portraitMedia.removeEventListener("change", updatePortrait);
      standaloneMedia.removeEventListener("change", updateInstalled);
      fullscreenMedia.removeEventListener("change", updateInstalled);
      document.removeEventListener("fullscreenchange", updateFullscreen);
      document.removeEventListener("fullscreenerror", fullscreenError);
      window.removeEventListener("pageshow", updateInstalled);
      unlockOrientation();
    };
  }, []);

  useEffect(() => {
    if (!playing) setHelpOpen(false);
  }, [playing]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (helpOpen && !dialog.open) dialog.showModal();
    if (!helpOpen && dialog.open) dialog.close();
  }, [helpOpen]);

  async function toggleFullscreen(landscape = false) {
    if (pending.current) return;
    if (!document.fullscreenElement && !fullscreenSupported) {
      setMessage("此浏览器不提供网页全屏功能；安装到主屏幕后打开，可在系统支持的应用显示模式中游玩。");
      setHelpOpen(true);
      return;
    }
    pending.current = true;
    setBusy(true);
    setMessage("");
    const exiting = Boolean(document.fullscreenElement) && !landscape;
    try {
      if (exiting) {
        await document.exitFullscreen();
        if (mounted.current) setFullscreen(Boolean(document.fullscreenElement));
        if (!unlockOrientation() && mounted.current) {
          setMessage(`已退出全屏，但系统未能解除横屏锁定。${manualRotation}`);
          setHelpOpen(true);
        }
        return;
      }

      // Keep this call in the button gesture; never request fullscreen from an effect.
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen({ navigationUI: "hide" });
      if (!mounted.current || document.fullscreenElement !== document.documentElement) return;
      setFullscreen(true);
      if (!landscape) {
        setHelpOpen(false);
        return;
      }
      if (!window.matchMedia(portraitQuery).matches) setHelpOpen(false);
      const orientation = screen.orientation as LandscapeOrientation | undefined;
      if (typeof orientation?.lock !== "function") {
        setMessage(`已进入全屏；此浏览器不支持自动锁定横屏。${manualRotation}`);
        setHelpOpen(window.matchMedia(portraitQuery).matches);
        return;
      }
      lockedOrientation.current = orientation;
      try {
        await orientation.lock("landscape");
        if (!mounted.current || !document.fullscreenElement) {
          // Exiting fullscreen can race the asynchronous orientation request.
          orientation.unlock();
          lockedOrientation.current = null;
        }
      } catch {
        if (lockedOrientation.current === orientation) lockedOrientation.current = null;
        if (mounted.current && document.fullscreenElement) {
          setMessage(`已进入全屏，但系统未允许锁定横屏。${manualRotation}`);
          setHelpOpen(window.matchMedia(portraitQuery).matches);
        }
      }
    } catch {
      if (mounted.current) {
        setFullscreen(Boolean(document.fullscreenElement));
        setMessage(exiting ? "未能退出全屏。请使用浏览器或系统的退出全屏操作（如 Esc 或返回手势）重试。" : fullscreenFailure);
        setHelpOpen(true);
      }
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  const modeLabel = fullscreen ? "当前为网页全屏" : installed === "fullscreen"
    ? "当前为应用全屏显示" : installed === "standalone" ? "当前为独立应用显示" : "当前为浏览器标签页";
  const fullscreenLabel = fullscreen ? "退出全屏" : "进入全屏";

  return (
    <div className="game-display-controls">
      {canToggleFullscreen && (
        <button type="button" onClick={() => void toggleFullscreen()} disabled={busy} aria-label={fullscreenLabel}>
          {busy ? "切换中…" : fullscreenLabel}
        </button>
      )}
      <button type="button" onClick={() => setHelpOpen(true)} title={modeLabel} aria-haspopup="dialog">
        应用显示
      </button>
      <dialog
        ref={dialogRef}
        className="game-display-dialog"
        aria-labelledby="game-display-title"
        aria-describedby="game-display-description"
        onCancel={() => setHelpOpen(false)}
      >
        <h2 id="game-display-title" tabIndex={-1} autoFocus>显示与全屏应用</h2>
        <p id="game-display-description">
          竖屏使用单屏缩略牌桌，横屏使用完整牌桌。普通全屏保持设备方向；也可以主动选择横屏全屏。
        </p>
        <p className="game-display-mode">{modeLabel}。{installed !== "browser" && "已在应用显示模式中；系统仍可能保留状态栏或导航条。"}</p>
        <p className="game-display-feedback" role="status" aria-live="polite" aria-atomic="true">{message}</p>
        {!fullscreenSupported && !fullscreen && <p>此浏览器不提供网页全屏按钮。可以安装到主屏幕后打开；网页不能代替系统强制隐藏状态栏。</p>}
        <p>{manualRotation}</p>
        {installed === "browser" ? (
          <ul className="game-display-install">
            <li><strong>iPhone / iPad：</strong>在 Safari 的「分享」菜单选择「添加到主屏幕」，再从主屏幕图标打开。若有「作为网页 App 打开」选项，请开启。</li>
            <li><strong>Android：</strong>在浏览器菜单选择「安装应用」或「添加到主屏幕」，按提示安装后从图标打开。菜单名称和是否支持安装取决于浏览器。</li>
          </ul>
        ) : (
          <p>当前应用模式由系统管理，不等同于网页请求的全屏，不能用网页按钮退出应用模式；请使用系统返回或主屏幕手势。</p>
        )}
        <div className="game-display-actions">
          {canToggleFullscreen && <button type="button" onClick={() => void toggleFullscreen()} disabled={busy}>{busy ? "切换中…" : fullscreenLabel}</button>}
          {fullscreenSupported && <button type="button" onClick={() => void toggleFullscreen(true)} disabled={busy}>横屏全屏</button>}
          {playing && <button type="button" onClick={onLeave}>离开牌桌</button>}
          <button type="button" onClick={() => setHelpOpen(false)}>关闭说明</button>
        </div>
      </dialog>
    </div>
  );
}
